/*
 * Copyright © 2017-2021 Skale Education Inc. - All Rights Reserved
 */

import React, { useState, useEffect, useCallback, useRef } from "react";
import { Image, TouchableOpacity, View } from "react-native";
import Resizer from "react-image-file-resizer";
import Rodal from "rodal";
import "rodal/lib/rodal.css";
import Webcam from "react-webcam";
import { ic_close } from "react-icons-kit/md/ic_close";
// import ImageUploading from "react-images-uploading";
import Icon from "react-icons-kit";
import { camera } from "react-icons-kit/fa/camera";
// import { image } from "react-icons-kit/fa/image";

import { asyncCallApi, callApi } from "config/UpduoService";
import { Button, FadeInOutView, LoadingDots, Text } from "UpduoLibrary";
import {
  // skaleLightGray,
  skaleBlack,
  skaleBlue,
  skaleRed,
  skalePrimaryBlue,
  skaleWhite,
  skaleWebTextDarkGray,
} from "config/UpduoColors";
import { b64toBlob } from "config/Utilities";
interface PhotoModalProps {
  isVisible: boolean;
  onDismiss: Function;
  updatePhoto: Function;
  userHasPhoto: boolean;
}

export default function PhotoModal({
  isVisible,
  onDismiss,
  updatePhoto,
  userHasPhoto,
}: PhotoModalProps) {
  const webcamRef = useRef<any>();
  // const [images, setImages] = useState([]);
  // const [currentView, setCurrentView] = useState("select");
  const [hasCaptured, setHasCaptured] = useState(false);
  const [capturedPhotoPath, setCapturedPhotoPath] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isWebcamEnabled, setIsWebcamEnabled] = useState(false);

  useEffect(() => {
    setIsWebcamEnabled(isVisible);
  }, [isVisible]);

  const _handleResizeFile = (file, width, height) => {
    const fileDataStripped = file.match(/,(.*)$/)![1];
    const fileDataBlob = b64toBlob(fileDataStripped, "image/jpeg");
    return new Promise((resolve) => {
      Resizer.imageFileResizer(
        fileDataBlob,
        width,
        height,
        "JPEG",
        100,
        0,
        (uri) => {
          resolve(uri as string);
        },
        "base64"
      );
    });
  };

  const _handleSaveProfilePhoto = async () => {
    const lgResponse = (await _handleResizeFile(
      capturedPhotoPath,
      300,
      300
    )) as string;
    const smResponse = (await _handleResizeFile(
      capturedPhotoPath,
      100,
      100
    )) as string;
    callApi(
      "getPhotoSignedUrls",
      undefined,
      (responseJson: { sm_signed_url: string; lg_signed_url: string }) => {
        _handleSubmitImage(
          responseJson.sm_signed_url,
          smResponse,
          () => {
            asyncCallApi("patchMePrimary", { has_profile_img_sm: true });
          },
          () => {
            _handleSubmitImage(
              responseJson.lg_signed_url,
              lgResponse,
              () => {
                asyncCallApi("patchMePrimary", {
                  has_profile_img_lg: true,
                });
              },
              _handleSuccessfulSubmission
            );
          }
        );
      }
    );
  };

  const _handleSuccessfulSubmission = () => {
    setCapturedPhotoPath("");
    setHasCaptured(false);
    setIsLoading(false);
    // setCurrentView("select");
    updatePhoto();
  };

  const _handleSubmitImage = (
    presignedUrl: string,
    fileData: string,
    success: Function,
    callback: Function
  ) => {
    const regex = /,(.*)$/;
    const fileDataStripped = regex.exec(fileData)![1];
    const fileDataBlob = b64toBlob(fileDataStripped, "image/jpeg");
    const s3headers = new Headers();
    s3headers.append("content-type", "image/jpeg");
    fetch(presignedUrl, {
      method: "PUT",
      headers: s3headers,
      body: fileDataBlob,
    })
      .then(() => {
        success();
        callback();
      })
      .catch(() => {
        callback();
      });
  };

  // const selectionView = (
  //   <Rodal
  //     visible={currentView == "select"}
  //     onClose={() => onDismiss()}
  //     customStyles={{
  //       width: 290,
  //       height: 150,
  //       borderRadius: 10,
  //       padding: 0,
  //     }}
  //   >
  //     <View
  //       style={{
  //         flexDirection: "column",
  //         justifyContent: "center",
  //         alignItems: "center",
  //         width: 290,
  //         height: 150,
  //       }}
  //     >
  //       <TouchableOpacity
  //         style={{
  //           justifyContent: "space-between",
  //           flexDirection: "row",
  //           alignItems: "center",
  //           padding: 28,
  //           width: 290,
  //           height: 75,
  //           borderBottomWidth: 1,
  //           borderBottomColor: skaleLightGray,
  //         }}
  //         onPress={() => {
  //           setCurrentView("camera");
  //         }}
  //       >
  //         <Text style={{ fontSize: 16 }}>{"Camera"}</Text>
  //         <Icon size={25} icon={camera} style={{ color: skaleBlack }} />
  //       </TouchableOpacity>
  //       <TouchableOpacity
  //         style={{
  //           justifyContent: "space-between",
  //           flexDirection: "row",
  //           alignItems: "center",
  //           padding: 28,
  //           width: 290,
  //           height: 75,
  //         }}
  //         onPress={() => {
  //           setCurrentView("library");
  //         }}
  //       >
  //         <Text style={{ fontSize: 16 }}>{"Select Photo"}</Text>
  //         <Icon size={25} icon={image} style={{ color: skaleBlack }} />
  //       </TouchableOpacity>
  //     </View>
  //   </Rodal>
  // );

  // const onChangeImageList = (imageList) => {
  //   // data for submit
  //   setImages(imageList);
  // };

  const _handleWebcamCapturePhoto = useCallback(() => {
    const imageSrc = webcamRef.current.getScreenshot();
    setHasCaptured(true);
    setCapturedPhotoPath(imageSrc);
    setIsWebcamEnabled(false);
  }, [webcamRef]);

  const cameraViewControlsHeight = 90;

  const cameraView = (
    <Rodal
      visible={isVisible}
      onClose={() => {
        onDismiss();
        // setCurrentView("select");
      }}
      customStyles={{
        width: 500,
        height: 520,
        borderRadius: 12,
      }}
    >
      <View
        style={{
          height: "100%",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        {userHasPhoto ? (
          <Text
            style={{
              fontWeight: "bold",
              fontSize: 20,
              textAlign: "center",
              paddingBottom: 40,
              fontFamily: "ObjectSans-Medium",
              color: skaleBlack,
            }}
          >
            {"Take a profile photo"}
          </Text>
        ) : (
          <>
            <Text
              style={{
                fontWeight: "bold",
                fontSize: 20,
                textAlign: "center",
                fontFamily: "ObjectSans-Medium",
                color: skaleBlack,
                paddingBottom: 5,
              }}
            >
              {"You don't have a profile photo!"}
            </Text>
            <Text
              style={{
                fontSize: 16,
                textAlign: "center",
                paddingBottom: 25,
                color: skaleWebTextDarkGray,
              }}
            >
              {"Take one now:"}
            </Text>
          </>
        )}
        {hasCaptured ? (
          <>
            <Image
              source={{
                uri: capturedPhotoPath,
              }}
              style={{
                height: 300,
                width: 300,
              }}
            />
            {isLoading ? (
              <View
                style={{
                  height: cameraViewControlsHeight,
                  justifyContent: "center",
                }}
              >
                <LoadingDots />
              </View>
            ) : (
              <FadeInOutView
                style={{
                  flexDirection: "row",
                  height: cameraViewControlsHeight,
                }}
              >
                <Button
                  marginTop={30}
                  onPress={() => {
                    setHasCaptured(false);
                    setIsWebcamEnabled(true);
                  }}
                  width={140}
                  height={60}
                  borderWidth={1}
                  borderColor={skaleRed}
                  color={skaleWhite}
                  textColor={skaleRed}
                  text={"Retake"}
                />
                <View style={{ width: 17 }} />
                <Button
                  marginTop={30}
                  onPress={() => {
                    setIsLoading(true);
                    _handleSaveProfilePhoto();
                  }}
                  width={140}
                  height={60}
                  borderColor={skalePrimaryBlue}
                  text={"Save"}
                />
              </FadeInOutView>
            )}
          </>
        ) : (
          <FadeInOutView style={{ alignItems: "center" }}>
            {isWebcamEnabled ? (
              <Webcam
                audio={false}
                height={300}
                width={300}
                ref={webcamRef}
                screenshotFormat={"image/jpeg"}
                videoConstraints={{
                  width: 300,
                  height: 300,
                  facingMode: "user",
                }}
                mirrored={true}
                style={{ backgroundColor: skaleBlack }}
              />
            ) : null}
            <View
              style={{
                height: cameraViewControlsHeight,
                justifyContent: "center",
              }}
            >
              <TouchableOpacity
                style={{
                  marginTop: 35,
                  height: 44,
                  width: 44,
                  backgroundColor: skaleBlue,
                  borderRadius: 22,
                  justifyContent: "center",
                  alignItems: "center",
                }}
                onPress={_handleWebcamCapturePhoto}
              >
                <div
                  style={{
                    color: skaleWhite,
                    paddingBottom: 2.5,
                  }}
                >
                  <Icon size={22} icon={camera} />
                </div>
              </TouchableOpacity>
            </View>
          </FadeInOutView>
        )}
      </View>
      <View style={{ position: "absolute", top: 14, right: 14 }}>
        <div style={{ color: skaleBlack }}>
          <Icon size={22} icon={ic_close} />
        </div>
      </View>
    </Rodal>
  );

  // const libraryView = (
  //   <Rodal
  //     visible={currentView == "library"}
  //     onClose={() => {
  //       onDismiss();
  //       setCurrentView("select");
  //     }}
  //     customStyles={{
  //       width: 500,
  //       height: 500,
  //       borderRadius: 12,
  //     }}
  //   >
  //     <View
  //       style={{
  //         flexDirection: "column",
  //         justifyContent: "center",
  //         alignItems: "center",
  //         padding: 30,
  //       }}
  //     >
  //       {isLoading ? (
  //         <View
  //           style={{
  //             paddingTop: 20,
  //           }}
  //         >
  //           <LoadingDots />
  //         </View>
  //       ) : (
  //         <ImageUploading
  //           value={images}
  //           onChange={onChangeImageList}
  //           dataURLKey="data_url"
  //         >
  //           {({ imageList, onImageUpload, onImageRemoveAll }) => {
  //             return (
  //               <div className="upload__image-wrapper">
  //                 <View
  //                   style={{
  //                     justifyContent: "center",
  //                   }}
  //                 >
  //                   {imageList.length > 0
  //                     ? setCapturedPhotoPath(imageList[0].data_url)
  //                     : setCapturedPhotoPath("")}
  //                   {imageList.map((image, index) => {
  //                     return (
  //                       <View
  //                         style={{
  //                           justifyContent: "center",
  //                           flexDirection: "row",
  //                         }}
  //                         key={index}
  //                       >
  //                         <div key={index} className="image-item">
  //                           <img src={image["data_url"]} alt="" height="200" />

  //                           <View
  //                             style={{
  //                               justifyContent: "center",
  //                               alignItems: "center",
  //                             }}
  //                           >
  //                             <Button
  //                               marginTop={30}
  //                               onPress={() => {
  //                                 setIsLoading(true);
  //                                 _handleSaveProfilePhoto();
  //                                 onImageRemoveAll();
  //                               }}
  //                               width={140}
  //                               height={60}
  //                               borderColor={skalePrimaryBlue}
  //                               text={"Save"}
  //                             />
  //                           </View>
  //                         </div>
  //                       </View>
  //                     );
  //                   })}
  //                 </View>
  //                 <View
  //                   style={{
  //                     padding: 20,
  //                     justifyContent: "center",
  //                   }}
  //                 >
  //                   <Button
  //                     marginTop={30}
  //                     onPress={() => {
  //                       onImageUpload();
  //                     }}
  //                     width={300}
  //                     height={60}
  //                     borderColor={skalePrimaryBlue}
  //                     text={"Upload New Image"}
  //                   />
  //                 </View>
  //               </div>
  //             );
  //           }}
  //         </ImageUploading>
  //       )}
  //     </View>
  //   </Rodal>
  // );
  return cameraView;
}
